/**
 * CommandResultsPanel - Display global search results grouped by collection
 */

import React from 'react';
import {
  User,
  GraduationCap,
  AlertTriangle,
  Building2,
  FileText,
  ClipboardList,
  HardHat,
  BarChart3,
} from 'lucide-react';
import { Pressable } from '../../common/motionSystem';
import { RESULT_GROUP_DISPLAY_LIMIT } from './searchResultNavigation';

const resultTypeIcons = {
  document: FileText,
  task: ClipboardList,
  worker: User,
  jobsite: Building2,
  permit: HardHat,
  training: GraduationCap,
  report: BarChart3,
};

const CommandResultsPanel = ({ results, onClose, onNavigate, selectedIndex = -1 }) => {
  if (!results) return null;

  const {
    query,
    groups = [],
    totalResults = 0,
    message,
  } = results;

  const handleResultClick = (item) => {
    if (!onNavigate || !item?.route) return;
    onNavigate(item.route, {
      id: item.id,
      resultType: item.resultType,
      metadata: item.metadata || {},
    });
  };

  let visibleIndex = 0;

  return (
    <div className="command-bar-results" data-testid="command-bar-results">
      <div className="command-bar-results-header">
        <span className="command-bar-results-title">
          Results for &quot;{query}&quot;
        </span>
        <span className="command-bar-results-count" data-testid="command-bar-results-count">
          {totalResults} found
        </span>
      </div>

      {message && (
        <div className="command-bar-results-message">
          {message}
        </div>
      )}

      {totalResults === 0 && (
        <div className="command-bar-empty" role="status" aria-live="polite">
          <div className="command-bar-empty-text">No results found</div>
        </div>
      )}

      {groups.map((group) => {
        const Icon = resultTypeIcons[group.resultType] || AlertTriangle;
        const groupKey = group.collection || group.resultType;

        if (group.status === 'error') {
          return (
            <div
              key={groupKey}
              className="command-bar-results-category"
              data-testid={`search-group-${groupKey}`}
              data-group-status="error"
            >
              <div className="command-bar-results-category-title">
                {group.label} ({group.resultTypeLabel})
              </div>
              <div
                className="command-bar-results-group-state command-bar-results-group-state-error"
                role="status"
                aria-live="polite"
              >
                Search unavailable for {group.label.toLowerCase()}
              </div>
            </div>
          );
        }

        if (!group.results || group.results.length === 0) {
          return (
            <div
              key={groupKey}
              className="command-bar-results-category"
              data-testid={`search-group-${groupKey}`}
              data-group-status="empty"
            >
              <div className="command-bar-results-category-title">
                {group.label} ({group.resultTypeLabel})
              </div>
              <div
                className="command-bar-results-group-state command-bar-results-group-state-empty"
                role="status"
                aria-live="polite"
              >
                No {group.label.toLowerCase()} found
              </div>
            </div>
          );
        }

        return (
          <div
            key={groupKey}
            className="command-bar-results-category"
            data-testid={`search-group-${groupKey}`}
            data-group-status="ok"
          >
            <div className="command-bar-results-category-title">
              {group.label} ({group.resultTypeLabel}) — {Math.min(group.results.length, RESULT_GROUP_DISPLAY_LIMIT)}
            </div>
            {group.results.slice(0, RESULT_GROUP_DISPLAY_LIMIT).map((item) => {
              const ItemIcon = resultTypeIcons[item.resultType] || Icon;
              const itemIndex = visibleIndex;
              visibleIndex += 1;
              const isSelected = selectedIndex === itemIndex;
              return (
                <Pressable
                  as="div"
                  key={item.id}
                  className={`command-bar-result-item${isSelected ? ' selected' : ''}`}
                  data-testid={`search-result-${item.id}`}
                  data-selected={isSelected ? 'true' : undefined}
                  onClick={() => handleResultClick(item)}
                >
                  <div className="command-bar-result-icon">
                    <ItemIcon size={14} />
                  </div>
                  <div className="command-bar-result-content">
                    <div className="command-bar-result-title">
                      <span>{item.title}</span>
                      <span className="command-bar-result-type-label">
                        {item.resultTypeLabel}
                      </span>
                    </div>
                    <div className="command-bar-result-meta">
                      {item.subtitle && <span>{item.subtitle}</span>}
                      {item.subtitle && item.status && <span> • </span>}
                      {item.status && (
                        <span
                          className="command-bar-result-status"
                          data-status={item.status}
                        >
                          {item.status}
                        </span>
                      )}
                    </div>
                  </div>
                </Pressable>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default CommandResultsPanel;
